import type { ReactNode } from "react";

import { useMemo, useState } from "react";

import clsx from "clsx";
import { MenuItem, MenuTrigger } from "react-aria-components";

import { TranslateGuard } from "../translate-guard";
import { FilterButton } from "./filter-button";
import { FilterContainer } from "./filter-container";
import { FilterMenu } from "./filter-menu";
import { Placeholder } from "./placeholder";

import styles from "./filter.module.css";

export type FilterItem = {
	label: ReactNode;
	value: string;
};

type FilterProps = {
	/**
	 * Controlled placeholder, if you want to show something different than the label of the selected item
	 */
	dynPlaceholder?: string;
	items: FilterItem[];
	onChange: (value: null | string) => void;
	placeholder: string;
	selected: null | string;
};

export function Filter({
	dynPlaceholder,
	items,
	onChange,
	placeholder,
	selected,
}: FilterProps): ReactNode {
	const [isOpen, setIsOpen] = useState(false);

	/**
	 * Items needs an id
	 */
	const enrichedItems = useMemo(() => {
		return items.map((item, i) => {
			return {
				id: String(i + 1),
				...item,
			};
		});
	}, [items]);

	return (
		<FilterContainer onChange={onChange} selected={selected}>
			<MenuTrigger isOpen={isOpen} onOpenChange={setIsOpen}>
				<FilterButton isOpen={isOpen} placeholder={placeholder} selected={selected}>
					<Placeholder
						dynPlaceholder={dynPlaceholder}
						items={enrichedItems}
						placeholder={placeholder}
						selected={selected}
					/>
				</FilterButton>
				<FilterMenu items={enrichedItems}>
					{(item) => {
						return (
							<MenuItem
								className={clsx(styles.dropdownOption, "body-2")}
								onAction={() => {
									return onChange(item.value);
								}}
							>
								<TranslateGuard>{item.label}</TranslateGuard>
							</MenuItem>
						);
					}}
				</FilterMenu>
			</MenuTrigger>
		</FilterContainer>
	);
}
